import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

export function Library() {
  const { t } = useLanguage();
  const [selectedFocus, setSelectedFocus] = useState('');

  const FOCUS_AREAS = [
    { id: '', label: t('all'), icon: '📚' },
    { id: 'confidence', label: t('confidence'), icon: '💪' },
    { id: 'resilience', label: t('resilience'), icon: '🌟' },
    { id: 'growth', label: t('growth'), icon: '🌱' },
    { id: 'gratitude', label: t('gratitude'), icon: '🙏' },
    { id: 'calm', label: t('calm'), icon: '🧘' },
    { id: 'success', label: t('success'), icon: '🏆' },
  ];

  const { data, isLoading } = useQuery({
    queryKey: ['library', selectedFocus],
    queryFn: () => api.getPhraseLibrary(selectedFocus || undefined),
  });

  const phrases = data?.phrases || [];
  const customPhrases = data?.customPhrases || [];

  return (
    <div className="container py-8">
      <div className="mb-8 fade-in">
        <h1 className="text-2xl mb-2">{t('affirmationLibrary')}</h1>
        <p className="text-gray-600">{t('librarySubtitle')}</p>
      </div>

      {/* Focus Area Filter */}
      <div className="flex flex-wrap gap-2 mb-8 fade-in" style={{ animationDelay: '0.1s' }}>
        {FOCUS_AREAS.map((area) => (
          <button
            key={area.id}
            onClick={() => setSelectedFocus(area.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedFocus === area.id
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {area.icon} {area.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">{t('loading')}</p>
        </div>
      ) : (
        <>
          {/* Custom/AI Generated Phrases */}
          {customPhrases.length > 0 && (
            <div className="mb-8 fade-in" style={{ animationDelay: '0.2s' }}>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span>✨</span> {t('yourGenerated')}
              </h2>
              <div className="grid gap-4">
                {customPhrases.map((phrase: any) => (
                  <PhraseCard key={phrase.id} phrase={phrase} isCustom t={t} />
                ))}
              </div>
            </div>
          )}

          {/* Standard Phrases */}
          <div className="fade-in" style={{ animationDelay: '0.3s' }}>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span>📚</span> {t('standardAffirmations')}
            </h2>
            {phrases.length === 0 ? (
              <div className="text-center py-12 card">
                <span className="text-4xl mb-4 block">🔍</span>
                <p className="text-gray-600">{t('noAffirmations')}</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {phrases.map((phrase: any) => (
                  <PhraseCard key={phrase.id} phrase={phrase} t={t} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const FOCUS_ICONS: Record<string, string> = {
  confidence: '💪',
  resilience: '🌟',
  growth: '🌱',
  gratitude: '🙏',
  calm: '🧘',
  success: '🏆',
};

function PhraseCard({ phrase, isCustom, t }: { phrase: any; isCustom?: boolean; t: (key: any) => string }) {
  const focusIcon = FOCUS_ICONS[phrase.focus_area] || '✨';
  const focusLabel = t(phrase.focus_area) || phrase.focus_area;

  return (
    <div className={`card hover:shadow-lg transition-shadow ${isCustom ? 'border-l-4 border-indigo-500' : ''}`}>
      <div className="flex items-start gap-4">
        <span className="text-2xl">{focusIcon}</span>
        <div className="flex-1">
          <p className="text-lg font-medium text-gray-800 mb-2">"{phrase.content}"</p>
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full capitalize">
              {focusLabel}
            </span>
            {isCustom ? (
              <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full">
                {t('aiGenerated')}
              </span>
            ) : (
              <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full">
                {formatAgeGroup(phrase.age_group, t)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatAgeGroup(ageGroup: string, t: (key: any) => string): string {
  switch (ageGroup) {
    case 'child_5_8': return t('child58');
    case 'child_9_12': return t('child912');
    case 'teen': return t('teen');
    case 'adult': return t('adult');
    default: return ageGroup;
  }
}
